import React, { useState } from 'react';
import { X, Shield, User, Award, DollarSign, FileText, CheckCircle2, Sparkles, Activity, Trash2 } from 'lucide-react';
import { GroupMemberProfile, FootballPosition, UserRole, FootballCardAttributes } from '../types';
import { calculatePositionOVR, getInitialPresetAttributes } from '../services/ratingCalculator';
import { NumberInput } from './NumberInput';

interface ManagePlayerModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: GroupMemberProfile;
  onUpdateMember: (updatedMember: GroupMemberProfile, correctionReason?: string) => void;
  onDeleteMember?: (memberId: string) => void;
}

export const ManagePlayerModal: React.FC<ManagePlayerModalProps> = ({
  isOpen,
  onClose,
  member,
  onUpdateMember,
  onDeleteMember
}) => {
  const [displayName, setDisplayName] = useState(member.displayName);
  const [role, setRole] = useState<UserRole>(member.role);
  const [jerseyNumber, setJerseyNumber] = useState(member.jerseyNumber);
  const [primaryPosition, setPrimaryPosition] = useState<FootballPosition>(member.primaryPosition);
  const [isActive, setIsActive] = useState(member.isActive);
  const [walletBalance, setWalletBalance] = useState(member.walletBalance);
  const [internalNotes, setInternalNotes] = useState(member.internalNotes || '');

  // Football Card Attributes
  const [card, setCard] = useState<FootballCardAttributes>({ ...member.footballCard });

  // Preset state
  const [selectedPreset, setSelectedPreset] = useState<string>('Custom');

  // Stats Corrections
  const [stats, setStats] = useState({ ...member.stats });
  const [correctionReason, setCorrectionReason] = useState('');

  // Every editable stat field (career totals + discipline + attendance).
  const STAT_FIELDS: {
    key: keyof GroupMemberProfile['stats'];
    label: string;
    color: string;
    allowDecimal?: boolean;
    min?: number;
    max?: number;
  }[] = [
    { key: 'matchesPlayed', label: 'Matches', color: 'text-emerald-400' },
    { key: 'wins', label: 'Wins', color: 'text-green-400' },
    { key: 'draws', label: 'Draws', color: 'text-slate-300' },
    { key: 'losses', label: 'Losses', color: 'text-red-400' },
    { key: 'goals', label: 'Goals', color: 'text-emerald-400' },
    { key: 'assists', label: 'Assists', color: 'text-blue-400' },
    { key: 'cleanSheets', label: 'Clean Sheets', color: 'text-teal-400' },
    { key: 'motmCount', label: 'MOTM', color: 'text-amber-400' },
    { key: 'hatTricks', label: 'Hat-Tricks', color: 'text-purple-400' },
    { key: 'yellowCards', label: 'Yellow Cards', color: 'text-yellow-400' },
    { key: 'redCards', label: 'Red Cards', color: 'text-red-500' },
    { key: 'ownGoals', label: 'Own Goals', color: 'text-orange-400' },
    { key: 'lateArrivals', label: 'Late Arrivals', color: 'text-amber-300' },
    { key: 'noShows', label: 'No-Shows', color: 'text-red-400' },
    { key: 'attendancePct', label: 'Attendance %', color: 'text-sky-400', min: 0, max: 100 },
    { key: 'winPct', label: 'Win %', color: 'text-green-400', min: 0, max: 100 },
    { key: 'averageRating', label: 'Avg Rating', color: 'text-indigo-400', allowDecimal: true, min: 1, max: 10 },
  ];

  const updateStatField = (key: keyof GroupMemberProfile['stats'], value: number) => {
    setStats((prev) => ({ ...prev, [key]: value }));
  };

  const handleDelete = () => {
    if (!onDeleteMember) return;
    if (!window.confirm(`Delete player "${member.displayName}" permanently? This removes their profile, login account and sessions. This cannot be undone.`)) return;
    onDeleteMember(member.id);
    onClose();
  };

  const handleCardAttrChange = (key: keyof FootballCardAttributes, val: number) => {
    const clamped = Math.max(1, Math.min(99, val || 0));
    const nextCard = { ...card, [key]: clamped };
    nextCard.overall = calculatePositionOVR(primaryPosition, nextCard);
    setCard(nextCard);
  };

  const handleApplyPreset = (presetName: any) => {
    setSelectedPreset(presetName);
    if (presetName !== 'Custom') {
      const presetAttrs = getInitialPresetAttributes(presetName);
      presetAttrs.overall = calculatePositionOVR(primaryPosition, presetAttrs);
      setCard(presetAttrs);
    }
  };

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    const updatedMember: GroupMemberProfile = {
      ...member,
      displayName,
      role,
      jerseyNumber,
      primaryPosition,
      isActive,
      walletBalance,
      internalNotes,
      footballCard: card,
      stats,
    };

    onUpdateMember(updatedMember, correctionReason || 'Organizer profile update');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/85 backdrop-blur-md">
      <div className="relative w-full max-w-2xl bg-slate-900 border border-slate-800 rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        
        {/* Header */}
        <div className="p-4 sm:p-5 border-b border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-400">
              <Shield className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-extrabold text-lg text-white">Manage Player: {member.displayName}</h3>
              <p className="text-xs text-slate-400">Organizer administrative profile controls</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl bg-slate-800 text-slate-400 hover:text-white transition">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSave} className="p-4 sm:p-6 overflow-y-auto space-y-6 flex-1 text-xs">
          
          {/* Section 1: Basic Role & Info */}
          <div className="space-y-3 bg-slate-950 p-4 rounded-2xl border border-slate-800">
            <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-emerald-400">
              Identity & Role Permissions
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Display Name</label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl p-2.5 font-medium focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Group Role</label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value as UserRole)}
                  className="w-full bg-slate-900 border border-slate-700 text-emerald-400 font-bold rounded-xl p-2.5 focus:outline-none"
                >
                  <option value="player">Player</option>
                  <option value="organizer">Organizer</option>
                  <option value="admin">Admin</option>
                  <option value="owner">Owner</option>
                  <option value="guest">Guest</option>
                </select>
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Jersey Number</label>
                <NumberInput
                  value={jerseyNumber}
                  onCommit={setJerseyNumber}
                  min={0}
                  className="w-full bg-slate-900 border border-slate-700 text-amber-300 font-mono font-bold rounded-xl p-2.5 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Primary Position</label>
                <select
                  value={primaryPosition}
                  onChange={(e) => setPrimaryPosition(e.target.value as FootballPosition)}
                  className="w-full bg-slate-900 border border-slate-700 text-white font-mono font-bold rounded-xl p-2.5 focus:outline-none"
                >
                  {['GK', 'CB', 'LB', 'RB', 'CDM', 'CM', 'CAM', 'LM', 'RM', 'LW', 'RW', 'ST'].map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Football Card Attributes */}
          <div className="space-y-3 bg-[#171c19] p-4 rounded-2xl border border-white/5">
            <div className="flex justify-between items-center">
              <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-[#a8c936]">
                Football Card Attributes
              </h4>
              <span className="text-sm font-extrabold font-mono text-white bg-[#1c221d] px-2.5 py-0.5 rounded-lg border border-white/10">
                OVR {card.overall}
              </span>
            </div>

            {/* Presets Selector */}
            <div>
              <span className="text-[11px] text-slate-400 font-medium block mb-1">Preset Starting Level:</span>
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {['Beginner', 'Casual', 'Average', 'Good', 'Very Good', 'Excellent', 'Custom'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleApplyPreset(p)}
                    className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition shrink-0 ${
                      selectedPreset === p
                        ? 'bg-[#a8c936] text-[#080b0a]'
                        : 'bg-[#080b0a] text-slate-400 border border-white/5 hover:text-white'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2.5">
              {[
                { label: 'Pace', key: 'pace' },
                { label: 'Shooting', key: 'shooting' },
                { label: 'Passing', key: 'passing' },
                { label: 'Dribbling', key: 'dribbling' },
                { label: 'Defending', key: 'defending' },
                { label: 'Physical', key: 'physical' },
              ].map((item) => (
                <div key={item.key} className="bg-slate-900 px-2 py-2 rounded-xl border border-slate-800 text-center">
                  <span className="block text-[11px] text-slate-400 font-bold mb-1">{item.label}</span>
                  <NumberInput
                    value={(card as any)[item.key]}
                    onCommit={(v) => handleCardAttrChange(item.key as any, v)}
                    min={1}
                    max={99}
                    emptyDefault={60}
                    className="w-full bg-slate-950 text-amber-300 font-mono font-black text-center rounded border border-slate-700 py-1.5 focus:outline-none"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Section 3: Match Stats Corrections (Logged to Activity Log) */}
          <div className="space-y-3 bg-slate-950 p-4 rounded-2xl border border-slate-800">
            <h4 className="font-extrabold text-white text-sm uppercase tracking-wider text-blue-400">
              Manual Stats & Attendance Corrections
            </h4>
            
            <div className="grid grid-cols-2 min-[480px]:grid-cols-3 sm:grid-cols-4 gap-2.5 text-center font-mono">
              {STAT_FIELDS.map((f) => (
                <div key={f.key} className="bg-slate-900 px-2 pt-2 pb-1.5 rounded-xl">
                  <span className="text-[10px] sm:text-[11px] text-slate-400 block font-sans leading-snug min-h-[12px]">{f.label}</span>
                  <NumberInput
                    value={stats[f.key] ?? 0}
                    onCommit={(v) => updateStatField(f.key, v)}
                    allowDecimal={f.allowDecimal}
                    min={f.min}
                    max={f.max}
                    className={`w-full bg-slate-950 ${f.color} font-bold text-center rounded border border-slate-700 px-1 py-1.5`}
                    aria-label={f.label}
                  />
                </div>
              ))}
            </div>

            <div>
              <label className="block text-slate-300 font-bold mb-1">
                Reason for Stats Correction (Will be posted to Activity Log)
              </label>
              <input
                type="text"
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
                placeholder="e.g. Corrected match goal tally from Friday game"
                className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl p-2.5 focus:outline-none"
              />
            </div>
          </div>

          {/* Section 4: Notes & Financial Balance */}
          <div className="space-y-3 bg-slate-950 p-4 rounded-2xl border border-slate-800">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-slate-300 font-bold mb-1">Wallet / Debt Balance (৳)</label>
                <NumberInput
                  value={walletBalance}
                  onCommit={setWalletBalance}
                  min={0}
                  className="w-full bg-slate-900 border border-slate-700 text-emerald-400 font-mono font-bold rounded-xl p-2.5 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-slate-300 font-bold mb-1">Internal Organizer Notes</label>
                <input
                  type="text"
                  value={internalNotes}
                  onChange={(e) => setInternalNotes(e.target.value)}
                  placeholder="Private organizer notes..."
                  className="w-full bg-slate-900 border border-slate-700 text-slate-300 rounded-xl p-2.5 focus:outline-none"
                />
              </div>
            </div>
          </div>

          {/* Footer Submit */}
          {onDeleteMember && (
            <button
              type="button"
              onClick={handleDelete}
              className="w-full py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 font-black text-sm rounded-xl border border-red-500/40 transition flex items-center justify-center gap-2"
            >
              <Trash2 className="w-4 h-4" /> Delete Player Permanently
            </button>
          )}
          <button
            type="submit"
            className="w-full py-3 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-sm rounded-xl transition shadow-lg shadow-emerald-500/20"
          >
            Save Player Changes & Log Activity 🛡️
          </button>
        </form>
      </div>
    </div>
  );
};
